import { useMemo, useState, useEffect, type ReactNode } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { ChevronRight, MoreVertical } from 'lucide-react';
import { CustomerStatusBadge } from '@/components/common/CustomerStatusBadge.js';
import { CloneTicketModal } from '@/components/tickets/CloneTicketModal.js';
import { WarrantyBadge } from '@/components/tickets/WarrantyBadge.js';
import { getCurrentStore } from '@/lib/api/stores.js';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth.js';
import { isManagerRole } from '@/lib/permissions.js';
import type { TicketListItem } from '@/lib/api/tickets.js';
import { formatDueCountdown, isTicketOverdue, formatOverdueAgo } from '@/lib/ticket-due.js';
import { prettyLabel } from '@/lib/format.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { cn } from '@/lib/utils.js';
import { StatusBadge, useTicketStatusCatalog } from '@/components/tickets/StatusBadge.js';
import { SYSTEM_STATUS_STAGES } from '@/lib/ticket-status-stages.js';

// Kanban columns are STAGES — automation groups by stage; cards show status name/color.
const STAGE_COLUMNS: Array<{
  id: string;
  label: string;
  dotClass: string;
  defaultStatus: string;
}> = [
  { id: 'intake', label: 'Intake', dotClass: 'bg-slate-400', defaultStatus: 'intake' },
  { id: 'in_progress', label: 'In Progress', dotClass: 'bg-blue-500', defaultStatus: 'in_repair' },
  { id: 'waiting', label: 'Waiting', dotClass: 'bg-amber-500', defaultStatus: 'waiting_parts' },
  { id: 'ready', label: 'Ready', dotClass: 'bg-green-500', defaultStatus: 'ready' },
];

const COLUMN_MAP = new Map(STAGE_COLUMNS.map((c) => [c.id, c]));
const COLUMN_IDS = new Set(STAGE_COLUMNS.map((c) => c.id));

function stageForSlug(slug: string, catalog?: Array<{ slug: string; stage: string }>): string {
  const fromCat = catalog?.find((s) => s.slug === slug)?.stage;
  if (fromCat) return fromCat;
  return SYSTEM_STATUS_STAGES[slug] ?? 'in_progress';
}

// ── Types ──────────────────────────────────────────────────────────────────────

type MenuState = { ticketId: string; mode: 'main' | 'status' | 'assign' } | null;

interface KanbanBoardProps {
  tickets: TicketListItem[];
  users: Array<{ id: string; firstName: string; lastName: string }>;
  onStatusChange: (ticketId: string, status: string) => Promise<void>;
  onAssign: (ticketId: string, technicianId: string) => Promise<void>;
  onOpen: (ticketId: string) => void;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function initials(name?: string | null) {
  if (!name) return '?';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

// ── Board ──────────────────────────────────────────────────────────────────────

export function TicketKanbanBoard({
  tickets,
  users,
  onStatusChange,
  onAssign,
  onOpen,
}: KanbanBoardProps) {
  const [cloneTicket, setCloneTicket] = useState<TicketListItem | null>(null);
  const user = useAuthStore((s) => s.user);
  const canClone = isManagerRole(user?.role ?? '');
  const storeQuery = useQuery({ queryKey: ['store-current'], queryFn: getCurrentStore });
  const vipLabel = storeQuery.data?.vipLabel;
  const flaggedLabel = storeQuery.data?.flaggedLabel;
  const [activeId, setActiveId] = useState<string | null>(null);
  const [menu, setMenu] = useState<MenuState>(null);
  const [, setTick] = useState(0);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  // Refresh due-countdown labels every minute
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const now = Date.now();

  const statusesQuery = useTicketStatusCatalog(true);
  const catalog = statusesQuery.data;

  const grouped = useMemo(() => {
    const map = new Map<string, TicketListItem[]>();
    for (const col of STAGE_COLUMNS) map.set(col.id, []);
    for (const ticket of tickets) {
      const stage = stageForSlug(ticket.status, catalog);
      if (COLUMN_IDS.has(stage)) {
        map.get(stage)!.push(ticket);
      }
    }
    return map;
  }, [tickets, catalog]);

  const activeTicket = activeId ? tickets.find((t) => t.id === activeId) ?? null : null;

  function openMenu(ticketId: string) {
    setMenu((prev) =>
      prev?.ticketId === ticketId ? null : { ticketId, mode: 'main' },
    );
  }

  function closeMenu() {
    setMenu(null);
  }

  function handleDragStart(event: DragStartEvent) {
    setActiveId(event.active.id as string);
    setMenu(null);
  }

  async function handleDragEnd(event: DragEndEvent) {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;

    const ticketId = active.id as string;
    const targetStage = over.id as string;

    const ticket = tickets.find((t) => t.id === ticketId);
    if (!ticket) return;

    const currentStage = stageForSlug(ticket.status, catalog);
    if (currentStage === targetStage) return;

    if (['closed', 'cancelled'].includes(targetStage)) {
      toastError('Cannot drag a ticket to a closed status');
      return;
    }

    const col = COLUMN_MAP.get(targetStage);
    const nextStatus = col?.defaultStatus ?? targetStage;
    toastSuccess(`${ticket.ticketNumber} moved to ${col?.label ?? prettyLabel(targetStage)}`);
    await onStatusChange(ticketId, nextStatus);
  }

  async function handleMenuStatusChange(ticketId: string, targetStage: string) {
    const ticket = tickets.find((t) => t.id === ticketId);
    closeMenu();
    if (!ticket) return;

    if (['closed', 'cancelled'].includes(targetStage)) {
      toastError('Cannot move ticket to a closed status');
      return;
    }

    const col = COLUMN_MAP.get(targetStage);
    const nextStatus = col?.defaultStatus ?? targetStage;
    toastSuccess(`${ticket.ticketNumber} moved to ${col?.label ?? prettyLabel(targetStage)}`);
    await onStatusChange(ticketId, nextStatus);
  }

  async function handleMenuAssign(ticketId: string, techId: string) {
    const ticket = tickets.find((t) => t.id === ticketId);
    closeMenu();
    if (!ticket) return;
    const tech = users.find((u) => u.id === techId);
    toastSuccess(
      `${ticket.ticketNumber} assigned to ${tech ? `${tech.firstName} ${tech.lastName}` : 'technician'}`,
    );
    await onAssign(ticketId, techId);
  }

  return (
    // Clicking outside any menu closes it
    <div onClick={closeMenu}>
      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: 'calc(100vh - 280px)' }}>
          {STAGE_COLUMNS.map((col) => (
            <KanbanColumn
              key={col.id}
              column={col}
              tickets={grouped.get(col.id) ?? []}
              users={users}
              now={now}
              menu={menu}
              catalog={catalog}
              onOpenMenu={openMenu}
              onOpen={onOpen}
              onMenuStatusChange={handleMenuStatusChange}
              onMenuAssign={handleMenuAssign}
              onSetMenuMode={(mode) =>
                setMenu((prev) => (prev ? { ...prev, mode } : prev))
              }
              vipLabel={vipLabel ?? null}
              flaggedLabel={flaggedLabel ?? null}
              canClone={canClone}
              onClone={(t) => setCloneTicket(t)}
            />
          ))}
        </div>

        <DragOverlay dropAnimation={null}>
          {activeTicket ? <CardOverlay ticket={activeTicket} now={now} /> : null}
        </DragOverlay>
      </DndContext>
      {cloneTicket ? (
        <CloneTicketModal
          ticketId={cloneTicket.id}
          ticketNumber={cloneTicket.ticketNumber}
          currentCustomerId={cloneTicket.customerId ?? null}
          open
          onClose={() => setCloneTicket(null)}
        />
      ) : null}
    </div>
  );
}

// ── Column ─────────────────────────────────────────────────────────────────────

interface ColumnProps {
  column: { id: string; label: string; dotClass: string };
  tickets: TicketListItem[];
  users: Array<{ id: string; firstName: string; lastName: string }>;
  now: number;
  menu: MenuState;
  catalog?: Array<{ slug: string; stage: string; name?: string; color?: string }> | undefined;
  onOpenMenu: (ticketId: string) => void;
  onOpen: (ticketId: string) => void;
  onMenuStatusChange: (ticketId: string, status: string) => void;
  onMenuAssign: (ticketId: string, techId: string) => void;
  onSetMenuMode: (mode: 'main' | 'status' | 'assign') => void;
  vipLabel?: string | null | undefined;
  flaggedLabel?: string | null | undefined;
  canClone?: boolean | undefined;
  onClone?: ((ticket: TicketListItem) => void) | undefined;
}

function KanbanColumn({
  column,
  tickets,
  users,
  now,
  menu,
  catalog,
  onOpenMenu,
  onOpen,
  onMenuStatusChange,
  onMenuAssign,
  onSetMenuMode,
  vipLabel,
  flaggedLabel,
  canClone,
  onClone,
}: ColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: column.id });

  return (
    <div
      className="flex shrink-0 flex-col rounded-2xl border border-slate-200 bg-slate-50 shadow-sm"
      style={{ width: 300 }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-slate-200 px-3 py-2.5">
        <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', column.dotClass)} />
        <span className="flex-1 truncate text-sm font-bold text-slate-800">{column.label}</span>
        <span className="shrink-0 rounded-full bg-slate-200 px-2 py-0.5 text-xs font-bold text-slate-600">
          {tickets.length}
        </span>
      </div>

      {/* Droppable body */}
      <div
        ref={setNodeRef}
        className={cn(
          'flex flex-col gap-2 overflow-y-auto p-2 transition-colors',
          isOver && 'ring-2 ring-inset ring-primary/50 bg-primary/5',
        )}
        style={{ minHeight: 80, maxHeight: 'calc(100vh - 320px)' }}
      >
        {tickets.map((ticket) => (
          <KanbanCard
            key={ticket.id}
            ticket={ticket}
            users={users}
            now={now}
            menu={menu}
            catalog={catalog}
            onOpenMenu={onOpenMenu}
            onOpen={onOpen}
            onMenuStatusChange={onMenuStatusChange}
            onMenuAssign={onMenuAssign}
            onSetMenuMode={onSetMenuMode}
                vipLabel={vipLabel}
                flaggedLabel={flaggedLabel}
                canClone={canClone}
                onClone={onClone}
          />
        ))}
        {tickets.length === 0 ? (
          <p className="py-6 text-center text-xs text-slate-400">No tickets</p>
        ) : null}
      </div>
    </div>
  );
}

// ── Card ───────────────────────────────────────────────────────────────────────

interface CardProps {
  ticket: TicketListItem;
  users: Array<{ id: string; firstName: string; lastName: string }>;
  now: number;
  menu: MenuState;
  catalog?: Array<{ slug: string; stage: string; name?: string; color?: string }> | undefined;
  onOpenMenu: (ticketId: string) => void;
  onOpen: (ticketId: string) => void;
  onMenuStatusChange: (ticketId: string, status: string) => void;
  onMenuAssign: (ticketId: string, techId: string) => void;
  onSetMenuMode: (mode: 'main' | 'status' | 'assign') => void;
  vipLabel?: string | null | undefined;
  flaggedLabel?: string | null | undefined;
  canClone?: boolean | undefined;
  onClone?: ((ticket: TicketListItem) => void) | undefined;
}

function KanbanCard({
  vipLabel,
  flaggedLabel,
  canClone,
  onClone,
  ticket,
  users,
  now,
  menu,
  catalog,
  onOpenMenu,
  onOpen,
  onMenuStatusChange,
  onMenuAssign,
  onSetMenuMode,
}: CardProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: ticket.id,
    data: { ticket },
  });

  const overdue = isTicketOverdue(ticket.promisedDate, ticket.status);
  const countdown = formatDueCountdown(ticket.promisedDate, ticket.status, now);
  const isMenuOpen = menu?.ticketId === ticket.id;

  const dueToneClass: Record<string, string> = {
    overdue: 'font-bold text-red-600',
    soon: 'font-semibold text-amber-600',
    today: 'font-semibold text-green-700',
    later: 'text-slate-500',
    none: 'text-slate-500',
  };

  return (
    <div
      ref={setNodeRef}
      style={{ width: 280 }}
      className={cn(
        'relative rounded-xl border shadow-sm',
        ticket.isWarranty ? 'border-violet-300 bg-violet-50' : 'border-slate-200 bg-white',
        overdue && 'border-red-300',
        isDragging && 'opacity-50',
      )}
    >
      {/* Priority bar */}
      {ticket.priority !== 'normal' && (
        <div
          className={cn(
            'h-1 rounded-t-xl',
            ticket.priority === 'urgent' && 'bg-orange-500',
            ticket.priority === 'rush' && 'bg-amber-400',
          )}
        />
      )}

      {/* Drag handle area + click to open */}
      <div
        {...listeners}
        {...attributes}
        className="cursor-grab px-3 pb-3 pt-2.5 active:cursor-grabbing"
        onClick={() => onOpen(ticket.id)}
      >
        {/* Top row: ticket number + badges */}
        <div className="flex items-start justify-between gap-1">
          <span className="inline-flex items-center gap-1.5 text-sm font-black text-slate-950">
            {ticket.ticketNumber}
            <WarrantyBadge isWarranty={ticket.isWarranty} />
            {ticket.importedFrom === 'repairdesk' ? (
              <span
                className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-600"
                title="Imported from RepairDesk"
              >
                RD
              </span>
            ) : null}
          </span>
          <div className="flex flex-wrap justify-end gap-1">
            <StatusBadge status={ticket.status} catalog={catalog as never} />
            {ticket.priority === 'urgent' && (
              <span className="rounded-full bg-orange-100 px-1.5 py-0.5 text-[10px] font-bold text-orange-700">
                URGENT
              </span>
            )}
            {ticket.priority === 'rush' && (
              <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">
                RUSH
              </span>
            )}
          </div>
        </div>

        {/* Device */}
        <p className="mt-1.5 truncate text-xs font-semibold text-slate-700">
          {ticket.deviceSummary ?? 'No device'}
        </p>

        {/* Repairs */}
        <p className="mt-0.5 truncate text-xs text-slate-500">
          {ticket.repairSummary ?? (ticket.repairs?.join(', ')) ?? 'No repairs'}
        </p>
        {ticket.customerComplaint?.trim() ? (
          <p className="mt-1 line-clamp-2 text-[11px] italic text-slate-500" title={ticket.customerComplaint}>
            “{ticket.customerComplaint.trim()}”
          </p>
        ) : null}

        {/* Customer + assignee */}
        <div className="mt-2 flex items-center gap-2 text-xs text-slate-600">
          <span className="flex min-w-0 flex-1 items-center gap-1 truncate">
            <span className="truncate">
              👤 {ticket.customerDisplayName ?? ticket.customerNameOverride ?? 'Customer'}
            </span>
            <CustomerStatusBadge
              status={ticket.customerStatus}
              vipLabel={vipLabel ?? null}
              flaggedLabel={flaggedLabel ?? null}
            />
          </span>
          {ticket.assignedTechName ? (
            <span
              className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-white"
              title={ticket.assignedTechName}
            >
              {initials(ticket.assignedTechName)}
            </span>
          ) : (
            <span className="shrink-0 text-slate-400">—</span>
          )}
        </div>

        {/* Due countdown */}
        <p className={cn('mt-1 text-xs', dueToneClass[countdown.tone] ?? 'text-slate-500')}>
          {countdown.label}
        </p>

        {overdue && ticket.promisedDate ? (
          <p className="mt-0.5 text-[10px] font-bold text-red-600">
            Overdue · {formatOverdueAgo(ticket.promisedDate, now)}
          </p>
        ) : null}
      </div>

      {/* ⋮ menu button (stops drag-click propagation) */}
      <div className="absolute right-1.5 top-1.5">
        <button
          type="button"
          className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          onClick={(e) => {
            e.stopPropagation();
            onOpenMenu(ticket.id);
          }}
          aria-label="Ticket menu"
        >
          <MoreVertical className="h-3.5 w-3.5" />
        </button>

        {/* Dropdown */}
        {isMenuOpen && (
          <div
            className="absolute right-0 top-6 z-50 min-w-[180px] rounded-xl border border-slate-200 bg-white py-1 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            {menu!.mode === 'main' && (
              <>
                <DropdownItem
                  onClick={() => {
                    onOpen(ticket.id);
                  }}
                >
                  Open Ticket
                </DropdownItem>

                <DropdownItem
                  onClick={() => onSetMenuMode('status')}
                  chevron
                >
                  Change Status
                </DropdownItem>

                <DropdownItem
                  onClick={() => onSetMenuMode('assign')}
                  chevron
                >
                  Assign Tech
                </DropdownItem>

                <div className="my-1 border-t border-slate-100" />

                <DropdownItem
                  onClick={() => {
                    if (ticket.status !== 'ready') {
                      onMenuStatusChange(ticket.id, 'ready');
                    }
                  }}
                  disabled={ticket.status === 'ready'}
                >
                  Mark Ready
                </DropdownItem>

                {canClone && onClone ? (
                  <DropdownItem
                    onClick={() => {
                      onClone(ticket);
                    }}
                  >
                    Clone Ticket
                  </DropdownItem>
                ) : null}
              </>
            )}

            {menu!.mode === 'status' && (
              <>
                <div className="flex items-center gap-1 px-3 py-1.5">
                  <button
                    type="button"
                    className="mr-1 text-xs text-slate-500 hover:text-slate-700"
                    onClick={() => onSetMenuMode('main')}
                  >
                    ← Back
                  </button>
                  <span className="text-xs font-semibold text-slate-600">Change Status</span>
                </div>
                <div className="border-t border-slate-100" />
                {STAGE_COLUMNS.filter((c) => c.id !== stageForSlug(ticket.status, catalog)).map((col) => (
                  <DropdownItem
                    key={col.id}
                    onClick={() => onMenuStatusChange(ticket.id, col.id)}
                  >
                    <span className={cn('mr-2 inline-block h-2 w-2 rounded-full', col.dotClass)} />
                    {col.label}
                  </DropdownItem>
                ))}
              </>
            )}

            {menu!.mode === 'assign' && (
              <>
                <div className="flex items-center gap-1 px-3 py-1.5">
                  <button
                    type="button"
                    className="mr-1 text-xs text-slate-500 hover:text-slate-700"
                    onClick={() => onSetMenuMode('main')}
                  >
                    ← Back
                  </button>
                  <span className="text-xs font-semibold text-slate-600">Assign Tech</span>
                </div>
                <div className="border-t border-slate-100" />
                {users.length === 0 ? (
                  <p className="px-3 py-2 text-xs text-slate-400">No users available</p>
                ) : null}
                {users.map((user) => (
                  <DropdownItem
                    key={user.id}
                    onClick={() => onMenuAssign(ticket.id, user.id)}
                  >
                    <span
                      className="mr-2 inline-flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[8px] font-bold text-white"
                    >
                      {initials(`${user.firstName} ${user.lastName}`)}
                    </span>
                    {user.firstName} {user.lastName}
                  </DropdownItem>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Drag Overlay Card ──────────────────────────────────────────────────────────

function CardOverlay({ ticket, now }: { ticket: TicketListItem; now: number }) {
  const countdown = formatDueCountdown(ticket.promisedDate, ticket.status, now);
  return (
    <div
      style={{ width: 280 }}
      className="rotate-2 rounded-xl border border-primary/30 bg-white px-3 py-2.5 shadow-xl"
    >
      <span className="text-sm font-black text-slate-950">{ticket.ticketNumber}</span>
      <WarrantyBadge isWarranty={ticket.isWarranty} />
      <p className="mt-1 truncate text-xs text-slate-600">
        {ticket.deviceSummary ?? 'No device'}
      </p>
      <p className="mt-0.5 text-xs text-slate-500">{countdown.label}</p>
    </div>
  );
}

// ── Dropdown helper ────────────────────────────────────────────────────────────

function DropdownItem({
  children,
  onClick,
  chevron,
  disabled,
}: {
  children: ReactNode;
  onClick?: () => void;
  chevron?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-1 px-3 py-1.5 text-left text-xs font-medium text-slate-700',
        'hover:bg-slate-50',
        disabled && 'cursor-not-allowed opacity-40',
      )}
    >
      <span className="flex-1">{children}</span>
      {chevron && <ChevronRight className="h-3 w-3 shrink-0 text-slate-400" />}
    </button>
  );
}
